/**
 * Info tooltip describing an account's stage / sub-stage and what is required to
 * advance it to the next stage. Rendered as a small info icon placed next to the
 * stage indicators in the AccountDetail header and Overview summary.
 *
 * `variant` controls what the tooltip shows:
 *   - 'full'     → phase + sub-stage + advancement requirement (header)
 *   - 'phase'    → phase description only
 *   - 'subStage' → sub-stage description + advancement requirement
 */

import React from 'react';
import {Info} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@common-web/components/ui/tooltip';
import type {LifecyclePhase, AccountsSubStage} from '@api/generated/client';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {findPositionBySystemSubStage, resolveAccountPosition} from '@common/accountsView';
import {
  PHASE_LABEL,
  PHASE_DESCRIPTION,
  SUB_STAGE_LABEL,
  SUB_STAGE_DESCRIPTION,
  SUB_STAGE_ADVANCE_REQUIREMENT,
  toActiveSubStage,
} from '@common-web/components/account/phaseLabels';

interface StageTooltipProps {
  phase: LifecyclePhase;
  subStage: AccountsSubStage;
  viewStageKey?: string | null;
  viewSubStageKey?: string | null;
  variant?: 'full' | 'phase' | 'subStage';
  className?: string;
}

function Block({
  label,
  text,
}: {
  label: string;
  text: string;
}): React.ReactElement {
  return (
    <div className='space-y-0.5'>
      <p className='font-semibold'>{label}</p>
      <p className='text-primary-foreground/80'>{text}</p>
    </div>
  );
}

export function StageTooltip({
  phase,
  subStage,
  viewStageKey,
  viewSubStageKey,
  variant = 'full',
  className,
}: StageTooltipProps): React.ReactElement {
  const showPhase = variant === 'full' || variant === 'phase';
  const showSubStage = variant === 'full' || variant === 'subStage';
  const showAdvance = variant === 'full' || variant === 'subStage';
  const config = useAccountsViewConfig();
  const position = resolveAccountPosition(config, {
    lifecyclePhase: phase,
    subStage,
    viewStageKey,
    viewSubStageKey,
  });

  // Labels come from the config; descriptions / advance copy are keyed off the
  // canonical phase + sub-stage the custom stage maps to.
  const phaseLabel = position?.stage.label ?? PHASE_LABEL[phase];
  const systemPhase = position?.stage.systemPhase ?? phase;
  const systemSubStage = position?.subStage.systemSubStage ?? subStage;
  // Describe the milestone being worked toward — the sub-stage every Accounts
  // surface displays (badges, lifecycle band). Mirrors the Accounts table /
  // detail header badge exactly: advance to the active milestone, but don't
  // advance onto a milestone this tenant's pipeline omits — fall back to the
  // account's own represented sub-stage so the label/description match the
  // config. The "To advance" line stays keyed by the raw sub-stage
  // (systemSubStage): its requirement is exactly the work that completes the
  // displayed milestone.
  const activeSubStage = toActiveSubStage(subStage);
  const activePosition =
    activeSubStage === subStage
      ? position
      : findPositionBySystemSubStage(config, activeSubStage);
  const displaySubStage = activePosition ? activeSubStage : subStage;
  const displayPosition = activePosition ?? position;
  const displaySubStageLabel =
    displayPosition?.subStage.label ?? SUB_STAGE_LABEL[displaySubStage];

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            aria-label='Stage details'
            className={cn(
              'inline-flex items-center text-muted-foreground transition-colors hover:text-foreground',
              className,
            )}
          >
            <Info className='h-3.5 w-3.5' />
          </button>
        </TooltipTrigger>
        <TooltipContent className='max-w-xs space-y-2 py-2 text-left leading-relaxed'>
          {showPhase ? (
            <Block label={phaseLabel} text={PHASE_DESCRIPTION[systemPhase]} />
          ) : null}
          {showSubStage ? (
            <Block
              label={displaySubStageLabel}
              text={SUB_STAGE_DESCRIPTION[displaySubStage]}
            />
          ) : null}
          {showAdvance ? (
            <div className='space-y-0.5 border-t border-primary-foreground/20 pt-2'>
              <p className='font-semibold'>To advance</p>
              <p className='text-primary-foreground/80'>
                {SUB_STAGE_ADVANCE_REQUIREMENT[systemSubStage]}
              </p>
            </div>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
